import React from 'react';
import { useTranslation } from 'react-i18next';
import { DiaryAttachments } from '@/components/DiaryAttachments';
import { DiaryHomeTasks } from '@/components/DiaryHomeTasks';
import { DiaryEvaluations } from '@/components/DiaryTable/DiaryEvaluations';
import { DiaryText } from '@/components/DiaryTextList/DiaryText';
import { CircleClock } from '@/components/Icons/CircleClock';
import { JournalNoteCard } from '@/pages/Overview/JournalNoteCard';
import type { JournalItem } from '@/types/journal';
import { formatWeekdayShortDate, isToday } from '@/utils/date';
import { formatJournalRecords } from '@/utils/journal';

const CARD_SURFACE =
  'rounded-xl bg-popover p-3 shadow-card contrast:border contrast:border-border';
const CARD = `flex flex-col gap-1 text-left ${CARD_SURFACE}`;

interface Props {
  items: JournalItem[];
}

export const JournalEntriesList: React.FC<Props> = ({ items }) => {
  const { t } = useTranslation();
  const records = formatJournalRecords(items);

  return (
    <div className="flex flex-col gap-4">
      {records.map(
        ({
          id,
          date,
          lessonNumber,
          subject,
          homeTasks,
          evaluation,
          behaviourEntry,
        }) => (
          <div key={id} className="flex flex-col gap-2">
            <div
              className={`flex items-center gap-1 ${isToday(date) ? 'text-primary' : 'text-subtle-foreground'}`}
            >
              <CircleClock className="size-4 shrink-0" />
              <span className="text-subhead-14">
                {formatWeekdayShortDate(date)}
                {!!lessonNumber &&
                  ` (${t('overview.journals.lessonNumber', { number: lessonNumber })})`}
              </span>
            </div>

            {!!behaviourEntry && <JournalNoteCard entry={behaviourEntry} />}

            {!!evaluation && (
              <DiaryEvaluations
                evaluations={[evaluation]}
                className="flex flex-col"
                itemClassName={CARD_SURFACE}
              />
            )}

            {!!subject && (
              <div className={CARD}>
                <p className="text-subhead-14 text-foreground">
                  {t('diary.table.topic')}
                </p>
                {!!subject.text && <DiaryText text={subject.text} />}
                {!subject.text && (
                  <p className="text-body-14 text-muted-foreground">
                    {t('diary.emptyDescription')}
                  </p>
                )}
                {!!subject.attachments?.length && (
                  <div className="pt-3">
                    <DiaryAttachments attachments={subject.attachments} />
                  </div>
                )}
              </div>
            )}

            {homeTasks.length > 0 && (
              <div className={CARD}>
                <p className="text-subhead-14 text-foreground">
                  {t('diary.table.homework')}
                </p>
                <DiaryHomeTasks homeTasks={homeTasks} />
              </div>
            )}
          </div>
        ),
      )}
    </div>
  );
};
